import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  distributorMe,
  distributorGetCards,
  distributorLookupCard,
  distributorMarkSold,
  distributorGetCardHistory,
} from "../services/api";
import { useAppConfig } from "../context/AppConfigContext";
import { useTranslation } from "../i18n/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import {
  LogOut,
  QrCode,
  Wifi,
  CreditCard,
  Search,
  X,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const STATUS_COLORS = {
  created: "bg-gray-100 text-gray-700",
  printed: "bg-blue-100 text-blue-700",
  ready: "bg-teal-100 text-teal-700",
  activated: "bg-orange-100 text-orange-700",
  sold: "bg-green-100 text-green-700",
  claimed: "bg-indigo-100 text-indigo-700",
};

export default function DistributorDashboardPage() {
  const [distributor, setDistributor] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortDesc, setSortDesc] = useState(true);

  // Scan state
  const [scanOpen, setScanOpen] = useState(false);
  const [scanStep, setScanStep] = useState("choose"); // choose, scanning_qr, scanning_nfc, found, not_found, not_yours, sold_success, error
  const [scanCard, setScanCard] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [scanSource, setScanSource] = useState("scan"); // "scan" | "list"
  const [scanError, setScanError] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [nfcReadAbort, setNfcReadAbort] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanningRef = useRef(false);

  const { appName, branding, loginHeader, loginLogo, cardLabels } = useAppConfig();
  const { t, language, i18nEnabled } = useTranslation();
  const navigate = useNavigate();

  const cardLabel = cardLabels?.[language]?.singular || cardLabels?.en?.singular || "Card";

  useEffect(() => {
    const token = localStorage.getItem("distributor_token");
    if (!token) {
      navigate("/distributor/login");
      return;
    }
    loadData();
  }, []); // eslint-disable-line

  const loadData = async () => {
    try {
      const [meRes, cardsRes] = await Promise.all([
        distributorMe(),
        distributorGetCards(),
      ]);
      setDistributor(meRes.data.distributor);
      setCards(cardsRes.data.cards);
    } catch {
      localStorage.removeItem("distributor_token");
      localStorage.removeItem("distributor");
      navigate("/distributor/login");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("distributor_token");
    localStorage.removeItem("distributor");
    navigate("/distributor/login");
  };

  // ── Scan helpers ─────────────────────────────────────────────

  const closeScan = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (nfcReadAbort) {
      nfcReadAbort.abort();
      setNfcReadAbort(null);
    }
    scanningRef.current = false;
    setScanOpen(false);
    setScanStep("choose");
    setScanCard(null);
    setScanHistory([]);
    setScanError("");
    setSalePrice("");
    setShowAllHistory(false);
  }, [nfcReadAbort]);

  const openCardDetail = (card) => {
    setScanCard(card);
    setScanHistory([]);
    setScanStep("found");
    setScanSource("list");
    setScanOpen(true);
    setSalePrice("");
    setShowAllHistory(false);
    distributorGetCardHistory(card.id).then((h) => setScanHistory(h.data.history)).catch(() => {});
  };

  const extractCode = (url) => {
    try {
      const u = new URL(url);
      const parts = u.pathname.split("/");
      const cIdx = parts.indexOf("c");
      if (cIdx !== -1 && parts[cIdx + 1]) return parts[cIdx + 1];
    } catch {}
    return null;
  };

  const lookupCode = async (code) => {
    try {
      const res = await distributorLookupCard(code);
      if (res.data.found) {
        setScanCard(res.data.card);
        setScanStep("found");
        distributorGetCardHistory(res.data.card.id).then((h) => setScanHistory(h.data.history)).catch(() => {});
      } else {
        setScanStep("not_found");
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setScanStep("not_yours");
      } else {
        setScanError(err.response?.data?.error || "Lookup failed");
        setScanStep("error");
      }
    }
  };

  // QR scanning
  const startQrScan = async () => {
    setScanStep("scanning_qr");
    scanningRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
      const tick = async () => {
        if (!scanningRef.current || !videoRef.current) return;
        try {
          const barcodes = await detector.detect(videoRef.current);
          if (barcodes.length > 0) {
            const raw = barcodes[0].rawValue;
            const code = extractCode(raw);
            if (code) {
              scanningRef.current = false;
              stream.getTracks().forEach((t) => t.stop());
              streamRef.current = null;
              await lookupCode(code);
              return;
            }
          }
        } catch {}
        if (scanningRef.current) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    } catch {
      setScanError("Camera not available");
      setScanStep("error");
    }
  };

  // NFC scanning
  const startNfcRead = async () => {
    setScanStep("scanning_nfc");
    try {
      const ndef = new window.NDEFReader();
      const ctrl = new AbortController();
      setNfcReadAbort(ctrl);
      await ndef.scan({ signal: ctrl.signal });
      ndef.addEventListener("reading", async ({ message }) => {
        for (const record of message.records) {
          if (record.recordType === "url") {
            const url = new TextDecoder().decode(record.data);
            const code = extractCode(url);
            if (code) {
              ctrl.abort();
              setNfcReadAbort(null);
              await lookupCode(code);
              return;
            }
          }
        }
        setScanStep("error");
        setScanError("No valid card URL in tag");
      });
    } catch {
      setScanError("NFC not available");
      setScanStep("error");
    }
  };

  // Mark as sold
  const handleMarkSold = async () => {
    if (!scanCard) return;
    try {
      const data = {};
      if (salePrice) data.sale_price = parseFloat(salePrice);
      const res = await distributorMarkSold(scanCard.id, data);
      setCards((prev) =>
        prev.map((c) => (c.id === scanCard.id ? res.data.card : c))
      );
      setScanCard(res.data.card);
      setScanStep("sold_success");
    } catch (err) {
      setScanError(err.response?.data?.error || "Failed to mark as sold");
      setScanStep("error");
    }
  };

  // ── Filtering ────────────────────────────────────────────────

  const filtered = cards
    .filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          c.code.toLowerCase().includes(q) ||
          (c.label || "").toLowerCase().includes(q) ||
          (c.profile?.profile_name || "").toLowerCase().includes(q)
        );
      }
      return true;
    })
    .sort((a, b) =>
      sortDesc
        ? new Date(b.created_at) - new Date(a.created_at)
        : new Date(a.created_at) - new Date(b.created_at)
    );

  const statusCounts = cards.reduce((acc, c) => {
    acc[c.status] = (acc[c.status] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {loginHeader === "logo" ? (
              <img
                src={loginLogo || branding.logo}
                alt={appName}
                className="h-8 object-contain"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            ) : (
              <span className="text-lg font-bold text-gray-900">{appName}</span>
            )}
            <span className="text-sm text-gray-500 hidden sm:inline">
              {distributor?.name}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {i18nEnabled && <LanguageSwitcher />}
            <button
              onClick={logout}
              className="p-2 text-gray-400 hover:text-red-500 transition"
              title={t("distributor.logout")}
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{cards.length}</p>
            <p className="text-xs text-gray-500">{t("distributor.total")}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-teal-600">{statusCounts.ready || 0}</p>
            <p className="text-xs text-gray-500">{t("distributor.available")}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{statusCounts.sold || 0}</p>
            <p className="text-xs text-gray-500">{t("distributor.sold")}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-indigo-600">{statusCounts.claimed || 0}</p>
            <p className="text-xs text-gray-500">{t("distributor.claimed")}</p>
          </div>
        </div>

        {/* Scan button + search */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => { setScanSource("scan"); setScanOpen(true); }}
            className="flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-blue-700 transition"
          >
            <QrCode className="h-4 w-4" /> {t("distributor.scan")}
          </button>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("distributor.search")}
              className="w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
            />
          </div>
        </div>

        {/* Status filter */}
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
          <button
            onClick={() => setStatusFilter("")}
            className={`px-3 py-1 rounded-full text-xs font-medium transition whitespace-nowrap ${
              !statusFilter ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {t("distributor.all")} ({cards.length})
          </button>
          {Object.entries(statusCounts).map(([status, count]) => (
            <button
              key={status}
              onClick={() => setStatusFilter(statusFilter === status ? "" : status)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition whitespace-nowrap ${
                statusFilter === status
                  ? STATUS_COLORS[status] || "bg-gray-100 text-gray-700"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {t(`distributor.status.${status}`)} ({count})
            </button>
          ))}
        </div>

        {/* Card list */}
        <div className="space-y-2">
          {filtered.map((c) => (
            <div
              key={c.id}
              onClick={() => openCardDetail(c)}
              className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center justify-between cursor-pointer hover:border-blue-300 hover:shadow-sm transition"
            >
              <div className="flex items-center gap-3 min-w-0">
                <CreditCard className="h-4 w-4 text-gray-400 shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-semibold text-gray-800">
                      {c.code}
                    </span>
                    {c.label && (
                      <span className="text-xs text-gray-500 truncate">
                        {c.label}
                      </span>
                    )}
                  </div>
                  {c.profile && (
                    <p className="text-xs text-gray-400 truncate">
                      {c.profile.profile_name}
                    </p>
                  )}
                </div>
              </div>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium shrink-0 ${
                  STATUS_COLORS[c.status] || "bg-gray-100 text-gray-700"
                }`}
              >
                {t(`distributor.status.${c.status}`)}
              </span>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-center text-sm text-gray-400 py-8">
              {search || statusFilter
                ? t("distributor.no_match")
                : t("distributor.no_cards")}
            </p>
          )}
        </div>
      </main>

      {/* Scan Modal */}
      {scanOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeScan}
          />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl mx-4 max-h-[90vh] overflow-y-auto">
            <button
              onClick={closeScan}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Choose scan method */}
            {scanStep === "choose" && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-1">
                  {t("distributor.scan_title")}
                </h2>
                <p className="text-sm text-gray-500 mb-6">
                  {t("distributor.scan_desc")}
                </p>
                <div className="space-y-3">
                  <button
                    onClick={startQrScan}
                    className="w-full flex items-center gap-4 rounded-xl border border-gray-200 p-4 hover:bg-gray-50 transition text-left"
                  >
                    <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-blue-100 text-blue-600">
                      <QrCode className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        {t("distributor.scan_qr")}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t("distributor.scan_qr_desc")}
                      </p>
                    </div>
                  </button>
                  {"NDEFReader" in window && (
                    <button
                      onClick={startNfcRead}
                      className="w-full flex items-center gap-4 rounded-xl border border-gray-200 p-4 hover:bg-gray-50 transition text-left"
                    >
                      <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-purple-100 text-purple-600">
                        <Wifi className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          {t("distributor.scan_nfc")}
                        </p>
                        <p className="text-xs text-gray-500">
                          {t("distributor.scan_nfc_desc")}
                        </p>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* QR scanning */}
            {scanStep === "scanning_qr" && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-4">
                  {t("distributor.scan_qr")}
                </h2>
                <div className="relative rounded-xl overflow-hidden bg-black aspect-square">
                  <video
                    ref={videoRef}
                    className="w-full h-full object-cover"
                    playsInline
                    muted
                  />
                </div>
                <p className="text-xs text-gray-500 text-center mt-3">
                  {t("distributor.scan_qr_hint")}
                </p>
              </div>
            )}

            {/* NFC scanning */}
            {scanStep === "scanning_nfc" && (
              <div className="text-center py-8">
                <div className="animate-pulse mb-4">
                  <Wifi className="h-16 w-16 text-purple-400 mx-auto" />
                </div>
                <h2 className="text-lg font-bold text-gray-900 mb-2">
                  {t("distributor.scan_nfc_reading")}
                </h2>
                <p className="text-sm text-gray-500">
                  {t("distributor.scan_nfc_hint")}
                </p>
              </div>
            )}

            {/* Card found */}
            {scanStep === "found" && scanCard && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  {scanSource === "scan" && <CheckCircle className="h-5 w-5 text-green-500" />}
                  <CreditCard className={`h-5 w-5 ${scanSource === "list" ? "text-gray-400" : "hidden"}`} />
                  <h2 className="text-lg font-bold text-gray-900">
                    {scanSource === "scan" ? t("distributor.card_found") : t("distributor.card_detail")}
                  </h2>
                </div>
                <div className="rounded-xl bg-gray-50 p-4 space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">{t("distributor.code")}</span>
                    <span className="font-mono font-semibold text-gray-800">
                      {scanCard.code}
                    </span>
                  </div>
                  {scanCard.label && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{t("distributor.label")}</span>
                      <span className="text-gray-800">{scanCard.label}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">{t("distributor.status_label")}</span>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        STATUS_COLORS[scanCard.status] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {t(`distributor.status.${scanCard.status}`)}
                    </span>
                  </div>
                  {scanCard.profile && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">
                        {t("distributor.linked_profile")}
                      </span>
                      <span className="text-gray-800">
                        {scanCard.profile.profile_name}
                      </span>
                    </div>
                  )}
                </div>

                {/* History timeline */}
                {(() => {
                  const hasCreatedEvent = scanHistory.some((h) => h.event === "created");
                  const fullTimeline = hasCreatedEvent ? scanHistory : [
                    ...scanHistory,
                    { id: "_created", event: "created", created_at: scanCard.created_at, actor: null, old_value: null, new_value: null },
                  ];
                  const timeline = showAllHistory ? fullTimeline : fullTimeline.slice(0, 5);
                  return fullTimeline.length > 0 && (
                    <div className="rounded-xl bg-gray-50 p-4 mb-4">
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-2">{t("distributor.history")}</p>
                      <div className="space-y-0">
                        {timeline.map((h, i) => (
                          <div key={h.id} className="flex items-start gap-3 relative">
                            <div className="flex flex-col items-center">
                              <div className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 shrink-0" />
                              {i < timeline.length - 1 && <div className="w-px flex-1 bg-gray-200 min-h-[20px]" />}
                            </div>
                            <div className="pb-3 min-w-0">
                              <p className="text-xs font-medium text-gray-700">
                                {t(`distributor.event.${h.event}`) || h.event}
                                {h.new_value && !["status_change"].includes(h.event) && (
                                  <span className="text-gray-500 font-normal"> — {h.new_value}</span>
                                )}
                                {h.event === "status_change" && (
                                  <span className="text-gray-500 font-normal"> {h.old_value} → {h.new_value}</span>
                                )}
                              </p>
                              <p className="text-[10px] text-gray-400">
                                {new Date(h.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                      {fullTimeline.length > 5 && (
                        <button
                          onClick={() => setShowAllHistory((v) => !v)}
                          className="w-full text-center text-xs font-medium text-blue-600 hover:text-blue-800 transition pt-1"
                        >
                          {showAllHistory ? t("distributor.history_show_less") : t("distributor.history_show_all", { count: fullTimeline.length })}
                        </button>
                      )}
                    </div>
                  );
                })()}

                {scanCard.status !== "sold" && scanCard.status !== "claimed" && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("distributor.sale_price")}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={salePrice}
                        onChange={(e) => setSalePrice(e.target.value)}
                        placeholder={t("distributor.sale_price_placeholder")}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                      />
                    </div>
                    <button
                      onClick={handleMarkSold}
                      className="w-full rounded-xl bg-green-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-green-700 transition"
                    >
                      {t("distributor.mark_sold")}
                    </button>
                  </div>
                )}

                {(scanCard.status === "sold" || scanCard.status === "claimed") && (
                  <p className="text-sm text-gray-500 text-center py-2">
                    {t("distributor.already_sold")}
                  </p>
                )}

                {scanSource === "scan" ? (
                  <button
                    onClick={() => {
                      setScanCard(null);
                      setScanHistory([]);
                      setScanStep("choose");
                      setSalePrice("");
                    }}
                    className="w-full mt-3 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
                  >
                    {t("distributor.scan_another")}
                  </button>
                ) : (
                  <button
                    onClick={closeScan}
                    className="w-full mt-3 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
                  >
                    {t("distributor.close")}
                  </button>
                )}
              </div>
            )}

            {/* Not found */}
            {scanStep === "not_found" && (
              <div className="text-center py-6">
                <AlertCircle className="h-12 w-12 text-amber-400 mx-auto mb-3" />
                <h2 className="text-lg font-bold text-gray-900 mb-2">
                  {t("distributor.not_found")}
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  {t("distributor.not_found_desc")}
                </p>
                <button
                  onClick={() => setScanStep("choose")}
                  className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
                >
                  {t("distributor.scan_another")}
                </button>
              </div>
            )}

            {/* Not yours */}
            {scanStep === "not_yours" && (
              <div className="text-center py-6">
                <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
                <h2 className="text-lg font-bold text-gray-900 mb-2">
                  {t("distributor.not_yours")}
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  {t("distributor.not_yours_desc")}
                </p>
                <button
                  onClick={() => setScanStep("choose")}
                  className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
                >
                  {t("distributor.scan_another")}
                </button>
              </div>
            )}

            {/* Sold success */}
            {scanStep === "sold_success" && (
              <div className="text-center py-6">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <h2 className="text-lg font-bold text-gray-900 mb-2">
                  {t("distributor.sold_success")}
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  {scanCard?.code} {t("distributor.sold_success_desc")}
                </p>
                <button
                  onClick={() => {
                    setScanCard(null);
    setScanHistory([]);
                    setScanStep("choose");
                    setSalePrice("");
                  }}
                  className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-blue-700 transition"
                >
                  {t("distributor.scan_another")}
                </button>
              </div>
            )}

            {/* Error */}
            {scanStep === "error" && (
              <div className="text-center py-6">
                <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
                <h2 className="text-lg font-bold text-gray-900 mb-2">
                  {t("distributor.error")}
                </h2>
                <p className="text-sm text-gray-500 mb-4">{scanError}</p>
                <button
                  onClick={() => setScanStep("choose")}
                  className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
                >
                  {t("distributor.try_again")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
